import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { injectIntl } from 'react-intl';
import moment from 'moment';
import { extendedServiceStopStatuses } from 'types/timetableServices';

import { localeToLanguageKey, findServiceStop, getSortedServiceStops, getTimetableStatusSummary } from 'utils/timetableServices';

import { newsSeverity, serviceStatuses } from 'types/timetableServices';

import Icon from 'components/ui/Icons';
import TimetableStopStatus from 'components/shared/TimetableStatus/TimetableStopStatus';

import StopSummary from 'components/shared/TimetableServiceStopsStatusSummary/StopSummary';
import { Timetable } from 'components/shared/TimetableServiceStopsStatusSummary/StopsSummary.style';

import { getTrainServiceStatusIcon, getTrainServiceStatusMessage } from './utils';

import {
  StyledArrow,
  StyledCarrier,
  StyledDirection,
  StyledMoreInformationContainer,
  StyledMoreInformationContent,
  StyledMoreInformationHeader,
  StyledStation,
  StyledStatus,
  StyledTime,
  StyledTrain,
  StyledTrainInfo,
  NewsContainer,
  StyledShowMoreInformation,
  StyledOverview
} from './LiveTrainsInformation.styles';

import Message from './Message';

const Train = ({ destination, model, origin, serviceStops, news, isFocused = false, intl: { formatMessage, locale } }) => {
  const languageKey = localeToLanguageKey({ locale });
  const { carrier, serviceName, serviceStatus, trainNumber } = model;
  const {
    station: { uic: originUic, name: originName }
  } = origin;
  const {
    station: { uic: destinationUic, name: destinationName }
  } = destination;

  const originStationName = originName[languageKey];
  const destinationStationName = destinationName[languageKey];

  const departureService = findServiceStop({ serviceStops, uic: originUic });
  const arrivalService = findServiceStop({ serviceStops, uic: destinationUic });

  const {
    model: { commercialDepartureDateTime, scheduledDepartureDateTime }
  } = departureService;
  const commercialDepartureTime = moment(commercialDepartureDateTime).format('HH:mm');
  const scheduledDepartureTime = moment(scheduledDepartureDateTime).format('HH:mm');

  const {
    model: { commercialArrivalDateTime, scheduledArrivalDateTime }
  } = arrivalService;
  const commercialArrivalTime = moment(commercialArrivalDateTime).format('HH:mm');
  const scheduledArrivalTime = moment(scheduledArrivalDateTime).format('HH:mm');

  const arrivalSummary = getTimetableStatusSummary({ serviceStop: arrivalService });
  const departureSummary = getTimetableStatusSummary({ serviceStop: departureService });

  const { extendedArrivalStatus } = arrivalSummary;
  const { extendedDepartureStatus } = departureSummary;

  const serviceStatusMessage = getTrainServiceStatusMessage(serviceStatus);
  const serviceStatusIcon = getTrainServiceStatusIcon(serviceStatus);

  const sortedServiceStops = getSortedServiceStops({ model, serviceStops });

  const [showMoreInformation, setShowMoreInformation] = useState(isFocused);
  const trainRef = useRef(null);

  useEffect(() => {
    if (isFocused) {
      trainRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [isFocused]);

  const testId = `train-${serviceName}`;

  const isBoardingOnly =
    arrivalSummary.extendedArrivalStatus === extendedServiceStopStatuses.CANCELLED &&
    !!arrivalSummary.extendedDepartureStatus &&
    arrivalSummary.extendedDepartureStatus !== extendedServiceStopStatuses.CANCELLED;

  const isBoardingNotPossible =
    !!departureSummary.extendedArrivalStatus &&
    departureSummary.extendedArrivalStatus !== extendedServiceStopStatuses.CANCELLED &&
    departureSummary.extendedDepartureStatus === extendedServiceStopStatuses.CANCELLED;

  const departureTimeStatus = serviceStatus === extendedServiceStopStatuses.CANCELLED ? serviceStatus : extendedDepartureStatus;
  const arrivalTimeStatus = serviceStatus === extendedServiceStopStatuses.CANCELLED ? serviceStatus : extendedArrivalStatus;

  return (
    <StyledTrain data-testid={testId} ref={trainRef}>
      <StyledCarrier>
        {carrier} {trainNumber}
      </StyledCarrier>
      <StyledTrainInfo>
        <StyledOverview>
          <StyledDirection>
            <StyledStation>{originStationName}</StyledStation>
            <StyledTime status={departureTimeStatus}>
              {moment(commercialDepartureTime).isValid() ? commercialDepartureTime : scheduledDepartureTime}
            </StyledTime>
            <StyledStatus>
              <TimetableStopStatus
                serviceStatus={serviceStatus}
                serviceStop={departureService}
                isBoardingNotPossible={isBoardingNotPossible}
                departure
              />
            </StyledStatus>
          </StyledDirection>
          <StyledArrow />
          <StyledDirection>
            <StyledStation>{destinationStationName}</StyledStation>
            <StyledTime status={arrivalTimeStatus}>
              {moment(commercialArrivalTime).isValid() ? commercialArrivalTime : scheduledArrivalTime}
            </StyledTime>
            <StyledStatus>
              <TimetableStopStatus serviceStatus={serviceStatus} serviceStop={arrivalService} isBoardingOnly={isBoardingOnly} arrival />
            </StyledStatus>
          </StyledDirection>
        </StyledOverview>

        <StyledMoreInformationHeader status={serviceStatus}>
          {serviceStatusIcon && <Icon icon={serviceStatusIcon} />}
          <StyledShowMoreInformation
            data-testid={`${testId}-show-more-information`}
            label={formatMessage({ id: serviceStatusMessage })}
            onClick={() => setShowMoreInformation(!showMoreInformation)}
          />
        </StyledMoreInformationHeader>
      </StyledTrainInfo>

      <StyledMoreInformationContainer visible={showMoreInformation}>
        <StyledMoreInformationContent>
          <NewsContainer>
            {news.map(({ model }, index) => (
              <Message key={index} {...model} />
            ))}
          </NewsContainer>
          <Timetable>
            {sortedServiceStops.map((serviceStop, index) => (
              <StopSummary serviceStatus={serviceStatus} serviceStop={serviceStop} key={index} />
            ))}
          </Timetable>
        </StyledMoreInformationContent>
      </StyledMoreInformationContainer>
    </StyledTrain>
  );
};

Train.propTypes = {
  destination: PropTypes.shape({
    station: PropTypes.shape({
      name: PropTypes.shape({
        en: PropTypes.string,
        de: PropTypes.string,
        fr: PropTypes.string,
        nl: PropTypes.string
      }),
      uic: PropTypes.string
    })
  }),
  model: PropTypes.shape({
    carrier: PropTypes.string,
    serviceName: PropTypes.string,
    serviceStatus: PropTypes.oneOf(Object.keys(serviceStatuses)),
    trainNumber: PropTypes.string
  }),
  news: PropTypes.arrayOf(
    PropTypes.shape({
      model: PropTypes.shape({
        severity: PropTypes.oneOf(Object.keys(newsSeverity)),
        commercial: PropTypes.shape({
          en: PropTypes.string,
          de: PropTypes.string,
          fr: PropTypes.string,
          nl: PropTypes.string
        }),
        description: PropTypes.shape({
          en: PropTypes.string,
          de: PropTypes.string,
          fr: PropTypes.string,
          nl: PropTypes.string
        }),
        title: PropTypes.shape({
          en: PropTypes.string,
          de: PropTypes.string,
          fr: PropTypes.string,
          nl: PropTypes.string
        })
      })
    })
  ),
  origin: PropTypes.shape({
    station: PropTypes.shape({
      name: PropTypes.shape({
        en: PropTypes.string,
        de: PropTypes.string,
        fr: PropTypes.string,
        nl: PropTypes.string
      }),
      uic: PropTypes.string
    })
  }),
  serviceStops: PropTypes.arrayOf(PropTypes.object),
  isFocused: PropTypes.bool,
  intl: PropTypes.shape({
    formatMessage: PropTypes.func.isRequired,
    locale: PropTypes.string.isRequired
  })
};

export default injectIntl(Train);
